import React from "react";
import {
    Box, Flex, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
    Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { getTypeLabel } from "./legalDocumentConstants";
import LegalStatusBadge from "./LegalStatusBadge";

/**
 * Renders the type cell showing document type label and metadata subtitle.
 * @param {object} props
 * @param {object} props.doc - Document data object
 * @param {boolean} props.isSelected - Whether the parent row is selected
 * @returns {React.ReactElement}
 */
function DocumentTypeCell({ doc, isSelected }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Td>
            <Stack spacing="2px">
                <Text fontSize="0.8rem" fontWeight={isSelected ? "700" : "500"} color={titleColor}>
                    {getTypeLabel(doc.type)}
                </Text>
                <Text fontSize="0.65rem" color={subColor}>
                    {doc.language?.toUpperCase()} · {dayjs(doc.updated_at || doc.created_at).format("DD/MM/YY")}
                </Text>
            </Stack>
        </Td>
    );
}

/**
 * Renders a single document row in the legal documents table.
 * @param {object} props
 * @param {object} props.doc - Document data object
 * @param {boolean} props.isSelected - Whether this row is currently selected
 * @param {function} props.onSelect - Callback when row is clicked
 * @returns {React.ReactElement}
 */
function DocumentRow({ doc, isSelected, onSelect }) {
    const monoColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    const selectedBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const hoverBg = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    return (
        <Tooltip label="Click to view details" placement="right">
            <Tr cursor="pointer" bg={isSelected ? selectedBg : "transparent"} _hover={{ bg: hoverBg }}
                onClick={() => onSelect(doc)}>
                <DocumentTypeCell doc={doc} isSelected={isSelected} />
                <Td fontSize="0.8rem" color={monoColor}>{doc.version}</Td>
                <Td><LegalStatusBadge status={doc.status} /></Td>
            </Tr>
        </Tooltip>
    );
}

/**
 * Renders the table body: loading spinner, empty state, or document rows.
 * @param {object} props
 * @param {Array} props.documents - Documents to display
 * @param {object|null} props.selectedDoc - Currently selected document
 * @param {function} props.setSelectedDoc - Selection handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement}
 */
function DocumentTableBody({ documents, selectedDoc, setSelectedDoc, loading }) {
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading) {
        return <Box textAlign="center" py="40px"><Spinner color="brand.500" /></Box>;
    }
    if (documents.length === 0) {
        return <Box textAlign="center" py="40px"><Text color={emptyColor} fontSize="sm">Aucun document trouvé</Text></Box>;
    }
    return (
        <TableContainer maxH="400px" overflowY="auto">
            <Table size="sm">
                <Thead><Tr><Th>Type</Th><Th>Ver.</Th><Th>Statut</Th></Tr></Thead>
                <Tbody>
                    {documents.map((doc) => (
                        <DocumentRow key={doc.id} doc={doc} isSelected={selectedDoc?.id === doc.id} onSelect={setSelectedDoc} />
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

/**
 * Renders the document list table with loading state and empty state.
 * @param {object} props
 * @param {Array} props.documents - List of legal documents to display
 * @param {object|null} props.selectedDoc - Currently selected document
 * @param {function} props.setSelectedDoc - Setter for selected document
 * @param {boolean} props.loading - Whether the list is loading
 * @returns {React.ReactElement}
 */
function ListDocumentTable({ documents, selectedDoc, setSelectedDoc, loading }) {
    return (
        <Flex direction="column">
            <DocumentTableBody documents={documents} selectedDoc={selectedDoc}
                setSelectedDoc={setSelectedDoc} loading={loading} />
        </Flex>
    );
}

export default ListDocumentTable;
